// eslint-disable-next-line no-use-before-define
import React, { useState } from 'react';
import { styled } from '@mui/system';
import CareplanButton from './CareplanButton';
import { StandardDialog } from 'shared-components/components';
import { CareplanTemplate } from '../../Interface';
import { useTheme, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { CurrentAppConfig } from '../../AppConfig';

const AnnotationText = styled('p')`
  font-size: 80%;
`;

const SuggestedTemplates = (props: any): JSX.Element => {
  const { tumourStream, onSelect, templates = [], title, subheading, generateFooter, type } = props;
  const theme = useTheme();
  const [modalIsOpen, setIsModalOpen] = useState(false);

  const onClickHandler = (template: CareplanTemplate) =>
    onSelect(tumourStream, template.name, template.id, '', type === 'freq' ? 'frequently_used' : type);

  return (
    <>
      <div>
        <Typography data-testid="page-sub-header" variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
          {title}
          {type === 'sigma' && (
            <HelpOutline
              color="info"
              sx={{ marginLeft: '6px', cursor: 'pointer' }}
              data-testid="sigma-question-mark-"
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </Typography>
        <Typography color={theme.palette.text.secondary} paddingTop={1} paddingBottom={1} variant="body1">
          {templates.length > 0 && subheading}
        </Typography>
      </div>
      {templates.length > 0 ? (
        <div className="suggested-careplan-grid">
          {templates.slice(0, 6).map((currTemplate: CareplanTemplate, index: number) => {
            const footer = generateFooter(currTemplate);
            return (
              <CareplanButton
                dataTestId="suggested-careplan-item"
                key={currTemplate.id}
                currentTemplate={currTemplate}
                className={'suggested-careplan-item'}
                index={index}
                footer={footer}
                onClickHandler={() => onClickHandler(currTemplate)}
              />
            );
          })}
        </div>
      ) : (
        <Typography
          data-testid="no-careplan-suggestions"
          color={theme.palette.text.secondary}
          variant="body1"
          paddingBottom={3}>
          {`No careplan suggestions for this ${CurrentAppConfig.DefaultValue.tumourName} stream.`}
        </Typography>
      )}
      <StandardDialog
        maxWidth="md"
        title="Sigma Suggestions"
        open={modalIsOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}>
        <>
          <p>
            Sigma suggests careplan templates based on the diagnosis data entered by analysing past prescriptions for
            patients with similar diagnoses.
          </p>
          <p>
            The yellow bars show the <strong>Sigma score</strong> (0.0 - 1.0) for each suggestion. These scores are
            calculated by Sigma and used to rank templates, with the highest scores shown first.
          </p>
          <p>
            A <strong>higher score</strong> means that more patients with a similar diagnosis have been prescribed a
            careplan similar* to the template in the past.
          </p>
          <p>
            The score would be 1.0 (full bar) for a template if in the past all patients with the same diagnosis were
            prescribed exactly this template.
          </p>
          <AnnotationText>*Similar in terms of treatment sites, phases, techniques, dose and fractions.</AnnotationText>
        </>
      </StandardDialog>
    </>
  );
};

export default SuggestedTemplates;
